import {FC} from 'react'
import {Booking, BookingStatus} from '../services/api'

const ACCENT = '#039540'
const INK = '#1b1b29'
const MUTED = '#7e8299'

const STATUS_TONE: Record<BookingStatus, {color: string; bg: string; label: string}> = {
  PENDING: {color: '#b8770a', bg: 'rgba(255,168,0,0.15)', label: 'Pending'},
  WAITLISTED: {color: '#7c4dff', bg: 'rgba(124,77,255,0.12)', label: 'Waitlisted'},
  APPROVED: {color: '#1f9d5b', bg: 'rgba(3,149,64,0.12)', label: 'Approved'},
  COMPLETED: {color: '#2b4eff', bg: 'rgba(43,78,255,0.12)', label: 'Completed'},
  REJECTED: {color: '#d9214e', bg: 'rgba(217,33,78,0.1)', label: 'Rejected'},
  CANCELLED: {color: '#7e8299', bg: 'rgba(126,130,153,0.15)', label: 'Cancelled'},
}

type Tone = 'green' | 'amber' | 'purple' | 'red' | 'blue' | 'gray'
interface Event {
  at: string | null
  title: string
  desc?: string
  tone: Tone
  /** Lifecycle phase for ordering — deposit precedes approval even if its
   *  timestamp lands a moment later (both happen in the approve action). */
  order: number
}

const DOT: Record<Tone, string> = {
  green: ACCENT,
  amber: '#b8770a',
  purple: '#7c4dff',
  red: '#d9214e',
  blue: '#2b4eff',
  gray: '#9aa0ad',
}

const fmt = (iso: string | null): string => {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, {dateStyle: 'medium', timeStyle: 'short'})
}

/** "1 month" / "3 months". */
const mo = (n: number | null): string => (n == null ? '' : `${n} month${n === 1 ? '' : 's'}`)

/** Short, human-quotable reference for a booking id — links a transfer pair. */
const ref = (id: string): string => `#${id.slice(0, 8).toUpperCase()}`

/** The effective end of a stay: fixed end, scheduled move-out, or check-out. */
export const stayEndDate = (b: Booking): string | null =>
  b.endDate ?? b.moveOutDate ?? (b.checkedOutAt ? new Date(b.checkedOutAt).toISOString().slice(0, 10) : null)

// Lifecycle phases — the canonical order of a booking's life. The deposit (1)
// always precedes approval (2), even though both are stamped during the same
// approve action and the deposit's timestamp can land a second later.
const PHASE = {
  REQUESTED: 0,
  DEPOSIT: 1,
  DECISION: 2, // approved / waitlisted / rejected / cancelled
  PAYMENT: 3, // rent payments
  CHECKED_IN: 4,
  DEPARTURE: 5,
  CHECKED_OUT: 6,
  REFUNDED: 7, // deposit returned (after the stay ends)
  PENDING: 8, // forward-looking "awaiting…" / "under review"
}

/** Derive the lifecycle timeline from the booking's current fields. */
function buildEvents(b: Booking, admin = false): Event[] {
  const ev: Event[] = []
  ev.push({at: b.createdAt, title: 'Booking requested', desc: b.bed ? `Bed ${b.bed.code} · ${b.startDate} → ${stayEndDate(b) ?? 'ongoing'}` : undefined, tone: 'gray', order: PHASE.REQUESTED})

  // The admin decision. A cancelled booking that was reviewed/checked in was
  // approved first, then cancelled — show BOTH (the cancellation is added below).
  const wasApproved =
    b.status === 'APPROVED' ||
    b.status === 'COMPLETED' ||
    !!b.checkedInAt ||
    (b.status === 'CANCELLED' && !!b.reviewedAt)
  if (wasApproved) {
    ev.push({at: b.reviewedAt, title: 'Approved by admin', desc: b.amount ? `Total $${b.amount} for ${mo(b.months)}` : undefined, tone: 'green', order: PHASE.DECISION})
  } else if (b.status === 'WAITLISTED') {
    ev.push({at: b.reviewedAt, title: 'Added to waitlist', desc: admin ? 'Renter is waitlisted — notify when the bed frees up.' : 'We’ll notify you when the bed frees up.', tone: 'purple', order: PHASE.DECISION})
  } else if (b.status === 'REJECTED') {
    ev.push({at: b.reviewedAt, title: 'Not approved', desc: b.rejectionReason ?? undefined, tone: 'red', order: PHASE.DECISION})
  }

  for (const p of b.payments.filter((p) => p.status === 'PAID')) {
    const isDeposit = /deposit/i.test(p.periodLabel ?? '')
    const isBedWash = /bed wash/i.test(p.periodLabel ?? '')
    // A deposit carried over from the renter's previous booking — the payment
    // rows were re-pointed here, so mark it as transferred-in rather than newly paid.
    const carriedIn = isDeposit && !!b.depositTransferredFromId
    ev.push({
      // Use createdAt (a real timestamp) for display — paidAt is a date-only
      // field that would render before the booking even existed. A carried-in
      // deposit keeps the PREVIOUS booking's timestamp, so anchor it to this
      // booking's start instead — otherwise it sorts before "Booking requested".
      at: carriedIn ? b.createdAt : p.createdAt,
      title: carriedIn
        ? `Deposit transferred in · $${p.amount}`
        : isDeposit
        ? `Deposit received · $${p.amount}`
        : isBedWash
        ? `Bed wash · $${p.amount}`
        : `Payment received · $${p.amount}`,
      desc: carriedIn
        ? `Carried over from the renter’s previous booking (ref ${ref(b.depositTransferredFromId!)}) — no new deposit collected · receipt ${p.receiptNumber}`
        : isDeposit
        ? `Refundable deposit · held as security, refunded at clean departure · receipt ${p.receiptNumber}`
        : isBedWash
        ? `${/hygiene|6-week|automatic/i.test(p.note ?? '') ? '6-week hygiene wash' : 'Requested by renter'} · via ${p.method} · receipt ${p.receiptNumber}`
        : `${p.periodLabel ? `${p.periodLabel} · ` : ''}via ${p.method} · receipt ${p.receiptNumber}`,
      tone: carriedIn ? 'purple' : isBedWash ? 'blue' : 'green',
      order: isDeposit ? PHASE.DEPOSIT : PHASE.PAYMENT,
    })
  }

  // Deposit carried IN from a prior booking. Normally the payment row moved here
  // and is rendered above; if it isn't present (e.g. the payment was re-pointed
  // elsewhere), still surface the transfer from the marker so it's never missing.
  const hasDepositPayment = b.payments.some((p) => p.status === 'PAID' && /deposit/i.test(p.periodLabel ?? ''))
  if (b.depositTransferredFromId && !hasDepositPayment) {
    ev.push({
      at: b.createdAt,
      title: 'Deposit transferred in',
      desc: `Refundable deposit carried over from the renter’s previous booking (ref ${ref(b.depositTransferredFromId)}) — no new deposit collected.`,
      tone: 'purple',
      order: PHASE.DEPOSIT,
    })
  }

  // Deposit carried OUT of this booking onto the renter's next booking — the
  // deposit payment moved with it, so this is the only trace left here.
  if (b.depositTransferredToId) {
    ev.push({
      at: null,
      title: 'Deposit transferred out',
      desc: `Refundable deposit carried over to the renter’s next booking (ref ${ref(b.depositTransferredToId)}) instead of being refunded.`,
      tone: 'purple',
      order: PHASE.REFUNDED,
    })
  }

  // Refunded deposit — no refundedAt field, so omit the time and place it after
  // the stay via its phase order.
  for (const p of b.payments.filter((p) => p.status === 'REFUNDED' && /deposit/i.test(p.periodLabel ?? ''))) {
    // The note carries the net refund + any itemized deductions/reasons.
    ev.push({at: null, title: 'Deposit refunded', desc: `${p.note ?? `Refundable deposit returned · $${p.amount}`} · receipt ${p.receiptNumber}`, tone: 'green', order: PHASE.REFUNDED})
  }

  if (b.checkedInAt) ev.push({at: b.checkedInAt, title: 'Checked in', desc: admin ? 'Renter is checked in.' : 'Welcome aboard — rest easy.', tone: 'blue', order: PHASE.CHECKED_IN})
  if (b.departureConfirmedAt) ev.push({at: b.departureConfirmedAt, title: 'Departure confirmed', desc: 'Renter confirmed permanent departure & key-fob return.', tone: 'purple', order: PHASE.DEPARTURE})
  if (b.checkedOutAt) ev.push({at: b.checkedOutAt, title: 'Checked out', desc: admin ? 'Stay completed — bed freed.' : 'Thanks for staying. Safe travels ✈️', tone: 'blue', order: PHASE.CHECKED_OUT})

  // Cancellation is terminal — always the LAST event. Use a null timestamp so it
  // floats to the end regardless of other events' (possibly out-of-order) times,
  // ordered before a later deposit refund. Independent of the approval branch, so
  // a booking approved/checked-in and then cancelled shows both, cancel last.
  if (b.status === 'CANCELLED') {
    ev.push({at: null, title: 'Cancelled', desc: admin ? 'Booking cancelled — bed freed.' : 'Your booking was cancelled.', tone: 'red', order: PHASE.CHECKED_OUT})
  }

  // Pending: review only starts once the refundable deposit is received, so
  // surface "awaiting deposit" until it's in, then "under review".
  if (b.status === 'PENDING') {
    const hasDeposit = b.payments.some((p) => p.status === 'PAID' && /deposit/i.test(p.periodLabel ?? ''))
    if (hasDeposit) {
      ev.push({at: null, title: 'Under review', desc: admin ? 'Review the request — approve, waitlist, or reject.' : 'An admin will review your request shortly.', tone: 'amber', order: PHASE.PENDING})
    } else if (admin) {
      ev.push({at: null, title: 'Awaiting renter’s $100 deposit', desc: 'Renter must pay the $100 refundable deposit before the 24-hour review begins.', tone: 'amber', order: PHASE.PENDING})
    } else {
      ev.push({at: null, title: 'Awaiting your $100 deposit', desc: 'Pay the $100 refundable deposit to start your 24-hour review.', tone: 'amber', order: PHASE.PENDING})
    }
  }

  // Sort by real timestamp first, so events read in the order they happened.
  // Lifecycle phase is only a tiebreaker — for events stamped at the same instant
  // (e.g. deposit + approval in one action) and for timeless events (refund,
  // transfer-out, "under review") which float to the end, ordered by phase.
  // Clamp every event to the booking's creation time so nothing (e.g. a
  // backdated payment) can ever sort above "Booking requested" — the genesis.
  const created = +new Date(b.createdAt)
  return ev.sort((a, z) => {
    const ta = a.at ? Math.max(+new Date(a.at), created) : Number.POSITIVE_INFINITY
    const tz = z.at ? Math.max(+new Date(z.at), created) : Number.POSITIVE_INFINITY
    if (ta !== tz) return ta - tz
    return a.order - z.order
  })
}

export const BookingStatusPill: FC<{status: BookingStatus}> = ({status}) => {
  const t = STATUS_TONE[status]
  return <span style={{fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', borderRadius: 999, padding: '4px 10px', color: t.color, background: t.bg}}>{t.label}</span>
}

export const PaidPill: FC<{paid: boolean}> = ({paid}) => (
  <span style={{fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', borderRadius: 999, padding: '4px 10px', ...(paid ? {color: '#1f9d5b', background: 'rgba(3,149,64,0.12)'} : {color: '#b8770a', background: 'rgba(255,168,0,0.15)'})}}>
    {paid ? 'Paid' : 'Unpaid'}
  </span>
)

const Timeline: FC<{events: Event[]}> = ({events}) => (
  <div style={{position: 'relative', paddingLeft: 4}}>
    {events.map((e, i) => (
      <div key={i} style={{display: 'flex', gap: 14, position: 'relative'}}>
        <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
          <span style={{width: 13, height: 13, borderRadius: '50%', background: DOT[e.tone], boxShadow: `0 0 0 4px ${DOT[e.tone]}22`, flexShrink: 0, marginTop: 3}} />
          {i < events.length - 1 && <span style={{flex: 1, width: 2, background: '#e9edf2', margin: '4px 0'}} />}
        </div>
        <div style={{paddingBottom: 22, flex: 1}}>
          <div style={{fontWeight: 700, color: INK, fontSize: 15}}>{e.title}</div>
          {e.at && <div style={{fontSize: 12, color: MUTED, marginTop: 2}}>{fmt(e.at)}</div>}
          {e.desc && <div style={{fontSize: 13, color: '#475569', marginTop: 4}}>{e.desc}</div>}
        </div>
      </div>
    ))}
  </div>
)

/** Reusable booking lifecycle timeline in a modal. Pass admin to use ops-facing copy. */
export const BookingTimelineModal: FC<{booking: Booking; onClose: () => void; admin?: boolean}> = ({booking: b, onClose, admin}) => (
  <div onClick={onClose} style={{position: 'fixed', inset: 0, background: 'rgba(8,10,14,0.5)', zIndex: 1200, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '50px 20px', overflowY: 'auto'}}>
    <div onClick={(e) => e.stopPropagation()} style={{background: '#fff', borderRadius: 16, width: '100%', maxWidth: 460, boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden'}}>
      <div style={{background: 'linear-gradient(135deg, #039540, #026e2f)', color: '#fff', padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
        <div>
          <div style={{fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.85}}>Booking timeline</div>
          <div style={{fontSize: 20, fontWeight: 800}}>Bed {b.bed?.code ?? '—'}</div>
          <div style={{fontSize: 13, opacity: 0.9, marginTop: 2}}>{b.startDate} → {stayEndDate(b) ?? 'ongoing'}{b.months ? ` · ${mo(b.months)}` : ' · monthly'}</div>
        </div>
        <button onClick={onClose} aria-label='Close' style={{background: 'rgba(255,255,255,0.18)', border: 'none', color: '#fff', width: 30, height: 30, borderRadius: '50%', fontSize: 18, lineHeight: 1, cursor: 'pointer'}}>×</button>
      </div>

      <div style={{padding: '20px 24px 6px'}}>
        <div style={{display: 'flex', gap: 8, marginBottom: 18}}>
          <BookingStatusPill status={b.status} />
          <PaidPill paid={b.amount != null ? b.paid : (Number(b.paidAmount ?? 0) > 0 || b.subscriptionStatus === 'active' || b.hasSubscription)} />
        </div>
        {b.amount && (() => {
          const total = Number(b.amount)
          const paidAmt = Number(b.paidAmount ?? 0) // rent paid (excludes deposit)
          const depositHeld = Number(b.depositHeld ?? 0)
          const balance = Math.max(0, total - paidAmt)
          return (
            <div style={{background: '#f8fafc', borderRadius: 10, padding: '10px 14px', marginBottom: 20, fontSize: 14}}>
              <div style={{display: 'flex', justifyContent: 'space-between'}}>
                <span style={{color: MUTED}}>{mo(b.months)} × ${b.unitPrice}/month</span>
                <span style={{fontWeight: 800, color: INK}}>${total.toFixed(2)}</span>
              </div>
              {paidAmt > 0 && (
                <div style={{display: 'flex', justifyContent: 'space-between', marginTop: 6}}>
                  <span style={{color: MUTED}}>Rent paid</span>
                  <span style={{fontWeight: 700, color: ACCENT}}>− ${paidAmt.toFixed(2)}</span>
                </div>
              )}
              <div style={{borderTop: '1px dashed #dbe2ea', margin: '8px 0'}} />
              <div style={{display: 'flex', justifyContent: 'space-between'}}>
                <span style={{fontWeight: 700, color: INK}}>Balance due</span>
                <span style={{fontWeight: 800, color: balance > 0 ? INK : ACCENT}}>${balance.toFixed(2)}</span>
              </div>
              {depositHeld > 0 && (
                <div style={{display: 'flex', justifyContent: 'space-between', marginTop: 8, paddingTop: 8, borderTop: '1px solid #eef2f7'}}>
                  <span style={{color: MUTED}}>Refundable deposit (held)</span>
                  <span style={{fontWeight: 700, color: INK}}>${depositHeld.toFixed(2)}</span>
                </div>
              )}
            </div>
          )
        })()}
        <Timeline events={buildEvents(b, admin)} />
      </div>

      <div style={{padding: '8px 24px 22px'}}>
        <button onClick={onClose} style={{width: '100%', fontWeight: 700, fontSize: 14, color: '#fff', background: ACCENT, border: 'none', borderRadius: 10, padding: '11px 0', cursor: 'pointer'}}>Close</button>
      </div>
    </div>
  </div>
)
